import type { CurvePoint } from "@/lib/tool-kit/chem-reactions/titration";

const WIDTH = 640;
const HEIGHT = 360;
const LEFT = 52;
const RIGHT = 18;
const TOP = 16;
const BOTTOM = 50;
const PLOT_W = WIDTH - LEFT - RIGHT;
const PLOT_H = HEIGHT - TOP - BOTTOM;
const X_TICKS = 5;
const Y_STEP = 2;
const DEFAULT_Y_MIN = 0;
const DEFAULT_Y_MAX = 14;

interface Marker {
  volume: number;
  pH: number;
}

/** A tidy tick spacing (1, 2, 5 × 10ⁿ) giving about `count` ticks over `span`. */
export function niceStep(span: number, count: number): number {
  const raw = span / count;
  const power = 10 ** Math.floor(Math.log10(raw));
  const fraction = raw / power;
  let nice = 10;
  if (fraction <= 1) {
    nice = 1;
  } else if (fraction <= 2) {
    nice = 2;
  } else if (fraction <= 5) {
    nice = 5;
  }
  return nice * power;
}

/**
 * pH against titrant volume as inline SVG. Colours come from the theme tokens so the
 * chart follows light and dark mode.
 */
export function TitrationChart({
  points,
  equivalence,
  half,
  label,
  xLabel,
  yLabel,
  format,
}: {
  points: readonly CurvePoint[];
  equivalence: Marker;
  half: Marker | null;
  label: string;
  xLabel: string;
  yLabel: string;
  format: (value: number) => string;
}) {
  const xMax = points.at(-1)?.volume ?? 1;
  const pHs = points.map((p) => p.pH);
  const yMin = Math.min(DEFAULT_Y_MIN, Math.floor(Math.min(...pHs)));
  const yMax = Math.max(DEFAULT_Y_MAX, Math.ceil(Math.max(...pHs)));
  const x = (value: number) => LEFT + (value / xMax) * PLOT_W;
  const y = (value: number) =>
    TOP + (1 - (value - yMin) / (yMax - yMin)) * PLOT_H;
  const path = points
    .map(
      (p, index) =>
        `${index === 0 ? "M" : "L"}${x(p.volume).toFixed(1)} ${y(p.pH).toFixed(1)}`
    )
    .join(" ");
  const yTicks: number[] = [];
  for (let value = yMin; value <= yMax; value += Y_STEP) {
    yTicks.push(value);
  }
  const xStep = niceStep(xMax, X_TICKS);
  const xTicks: number[] = [];
  for (let value = 0; value <= xMax + xStep / 1e6; value += xStep) {
    xTicks.push(value);
  }
  return (
    <svg
      aria-label={label}
      className="h-auto w-full max-w-2xl text-muted-foreground"
      role="img"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
    >
      <title>{label}</title>
      {yTicks.map((tick) => (
        <g key={`y${tick}`}>
          <line
            className="stroke-border"
            x1={LEFT}
            x2={WIDTH - RIGHT}
            y1={y(tick)}
            y2={y(tick)}
          />
          <text
            className="fill-current text-xs"
            dominantBaseline="middle"
            textAnchor="end"
            x={LEFT - 8}
            y={y(tick)}
          >
            {format(tick)}
          </text>
        </g>
      ))}
      {xTicks.map((tick) => (
        <g key={`x${tick}`}>
          <line
            className="stroke-border"
            x1={x(tick)}
            x2={x(tick)}
            y1={TOP + PLOT_H}
            y2={TOP + PLOT_H + 5}
          />
          <text
            className="fill-current text-xs"
            textAnchor="middle"
            x={x(tick)}
            y={TOP + PLOT_H + 20}
          >
            {format(tick)}
          </text>
        </g>
      ))}
      <line
        className="stroke-muted-foreground"
        x1={LEFT}
        x2={LEFT}
        y1={TOP}
        y2={TOP + PLOT_H}
      />
      <line
        className="stroke-muted-foreground"
        x1={LEFT}
        x2={WIDTH - RIGHT}
        y1={TOP + PLOT_H}
        y2={TOP + PLOT_H}
      />
      <line
        className="stroke-muted-foreground"
        strokeDasharray="4 4"
        x1={x(equivalence.volume)}
        x2={x(equivalence.volume)}
        y1={TOP}
        y2={TOP + PLOT_H}
      />
      <path
        className="stroke-primary"
        d={path}
        fill="none"
        strokeLinejoin="round"
        strokeWidth={2.5}
      />
      <circle
        className="fill-primary"
        cx={x(equivalence.volume)}
        cy={y(equivalence.pH)}
        r={5}
      />
      {half ? (
        <circle
          className="fill-background stroke-primary"
          cx={x(half.volume)}
          cy={y(half.pH)}
          r={5}
          strokeWidth={2}
        />
      ) : null}
      <text
        className="fill-current text-sm"
        textAnchor="middle"
        x={LEFT + PLOT_W / 2}
        y={HEIGHT - 8}
      >
        {xLabel}
      </text>
      <text
        className="fill-current text-sm"
        textAnchor="middle"
        transform={`rotate(-90 14 ${TOP + PLOT_H / 2})`}
        x={14}
        y={TOP + PLOT_H / 2}
      >
        {yLabel}
      </text>
    </svg>
  );
}
